import type { RootState } from '~/store/store.ts';
import {
  emptyFatca,
  TFatca,
  TPepEntity,
  TPepInStore,
} from '~/modules/identification/forms/AdditionalInformation/types';
import {
  TGeneralInfo,
  TGeneralInfoLegalResident,
  TGeneralInfoSoleTrader,
} from '~/modules/identification/forms/GeneralInfo/types';
import { TManagersAndRepresentatives } from '~/modules/identification/forms/ManagersAndRepresentatives/types';
import { TOwnershipStructure } from '~/modules/identification/forms/OwnershipStructure/types';
import { TPurposeOfBusinessRelations } from '~/modules/identification/forms/BusinessRelations/types';
import { TDocumentsInStore } from '~/modules/identification/forms/Documents/types';
import { TManagmentStructure } from '~/modules/identification/forms/ManagementStructure/types';
import { TGettingStarted } from '~/modules/identification/forms/GettingStarted/types';
import { TRelatedCompanies } from '~/modules/identification/forms/RelatedCompanies/types';
import { TTaxResidencyAdditionalInformation } from '~/modules/identification/forms/TaxResidencyAdditionalInformation/types.ts';
import { TFatcaAdditionalInformation } from '~/modules/identification/forms/FatcaAdditionalInformation/types.ts';
import { EMPTY_FATCA_ADDITIONAL_INFORMATION } from '~/modules/identification/forms/FatcaAdditionalInformation/constants/constants.ts';

export const selectUsers = (state: RootState) => state.users;

export const selectOTP = (state: RootState) => state.otpVerification;

export const selectFullName = (state: RootState) =>
  (state.identification.formData.generalInfo as TGeneralInfoSoleTrader).fullName;

export const selectPepInStore = (state: RootState) =>
  state.identification.formData.additionalInformation as TPepInStore | undefined;

export const selectAdditionalInformation = (state: RootState): TPepEntity[] =>
  selectPepInStore(state)?.additionalInformation ?? [];

export const selectAdditionalInformationFatca = (state: RootState): TFatca =>
  selectPepInStore(state)?.fatca ?? emptyFatca;

export const selectAdditionalInformationIsForeignFinancialUnderFATCA = (
  state: RootState
): boolean | null => selectPepInStore(state)?.fatca?.isForeignFinancialUnderFATCA ?? null;

export const selectOwners = (state: RootState) =>
  (state.identification.formData.ownershipStructure as TOwnershipStructure)?.owners;

export const selectIsReidentification = (state: RootState) =>
  state.identification.isReidentification;

export const selectApplicationId = (state: RootState) =>
  state.identification.formData.applicationId;

export const selectClientType = (state: RootState) => state.identification?.formData?.type;

export const selectPrevAdditionalInformation = (state: RootState) =>
  state.identification?.formDataBack?.additionalInformation as TPepInStore;

export const selectPurposeOfBusinessRelations = (state: RootState) =>
  state.identification.formData.purposeOfBusinessRelations as TPurposeOfBusinessRelations;

export const selectPrevBusinessRelations = (state: RootState) =>
  state.identification.formDataBack?.purposeOfBusinessRelations;

export const selectManagersAndRepresentatives = (state: RootState) =>
  state.identification?.formData?.managersAndRepresentatives as TManagersAndRepresentatives;

export const selectPrevManagersAndRepresentatives = (state: RootState) =>
  state.identification?.formDataBack?.managersAndRepresentatives as TManagersAndRepresentatives;

export const selectIdentification = (state: RootState) => state.identification;

export const selectGeneralInfo = (state: RootState) =>
  state.identification.formData?.generalInfo as TGeneralInfo;

export const selectGeneralInfoSoleTrader = (state: RootState) =>
  state.identification.formData?.generalInfo as TGeneralInfoSoleTrader;

export const selectGeneralInfoAddressSoleTrader = (state: RootState) =>
  (state.identification.formData?.generalInfo as TGeneralInfoSoleTrader)?.address;

export const selectDocuments = (state: RootState) =>
  state.identification.formData?.documents as TDocumentsInStore;

export const selectOwnershipStructure = (state: RootState) =>
  state.identification.formData?.ownershipStructure as TOwnershipStructure;

export const selectPrevOwnershipStructure = (state: RootState) =>
  state.identification.formData?.ownershipStructure as TOwnershipStructure;

export const selectManagmentStructure = (state: RootState) =>
  state.identification.formData?.managementStructure as TManagmentStructure;

export const selectPrevManagmentStructure = (state: RootState) =>
  state.identification.formDataBack?.managementStructure as TManagmentStructure;

export const selectIsAccountHolder = (state: RootState) =>
  (state.identification.formData?.managementStructure as TManagmentStructure)?.isAccountHolder;

export const selectFormManagementStructure = (state: RootState) =>
  state.identification.formData?.managementStructure as TManagmentStructure;

export const selectHasForeignTaxResidency = (state: RootState) =>
  (state.identification.formData?.additionalInformation as TPepInStore)?.additionalInformation?.[0]
    ?.hasForeignTaxResidency;

export const selectPrevFinancialState = (state: RootState) =>
  state.identification.formDataBack?.financialState;

export const selectFinancialState = (state: RootState) =>
  state.identification.formData?.financialState;

export const selectCompletedForms = (state: RootState) =>
  state.identification.formData.completedForms;

export const selectGettingStarted = (state: RootState) =>
  state.identification.formData.gettingStarted as TGettingStarted;

export const selectGettingStartedBack = (state: RootState) =>
  state.identification.formDataBack?.gettingStarted as TGettingStarted;

export const selectGettingStartedIdentNumber = (state: RootState) =>
  (state.identification.formData.gettingStarted as TGettingStarted)?.identificationNumber;

export const selectRelatedCompanies = (state: RootState) =>
  state.identification.formData.relatedCompanies as TRelatedCompanies;

export const selectPrevRelatedCompanies = (state: RootState) =>
  state.identification.formDataBack?.relatedCompanies as TRelatedCompanies;

export const selectContragentType = (state: RootState) =>
  (state.identification.formData?.gettingStarted as TGettingStarted)?.contragentType;

export const selectIsBeneficiariesAbsent = (state: RootState) =>
  (state.identification?.formData?.ownershipStructure as TOwnershipStructure)
    ?.isBeneficiariesAbsent;

export const selectIdentificationError = (state: RootState) => state.identification?.error;
export const selectIsOtherTaxResident = (state: RootState) =>
  (state.identification?.formData?.generalInfo as TGeneralInfoLegalResident)?.isOtherTaxResident;

export const selectIsControllingPersonTaxResidentOtherJurisdiction = (state: RootState) =>
  (state.identification?.formData?.generalInfo as TGeneralInfoLegalResident)
    ?.isControllingPersonTaxResidentOtherJurisdiction;

export const selectShortNameUkr = (state: RootState) =>
  (state.identification?.formData?.generalInfo as TGeneralInfoLegalResident)?.shortNameUkr;

export const selectGeneralInfoAddress = (state: RootState) =>
  (state.identification?.formData?.generalInfo as TGeneralInfoLegalResident)?.address;

export const selectGeneralInfoFullNameUkr = (state: RootState) =>
  (state.identification?.formData?.generalInfo as TGeneralInfoLegalResident)?.fullNameUkr;

export const selectGeneralInfoFullNameEng = (state: RootState) =>
  (state.identification?.formData?.generalInfo as TGeneralInfoLegalResident)?.fullNameEng;

export const selectTaxResidencyAdditionalInformation = (state: RootState) =>
  state.identification?.formData
    ?.taxResidencyAdditionalInformation as TTaxResidencyAdditionalInformation;

export const selectIsTaxResidencyAdditionalInformationCompleted = (state: RootState) =>
  state.identification.formData?.completedForms?.isTaxResidencyAdditionalInformationCompleted;

export const selectControllingPersons = (state: RootState) =>
  (
    state.identification?.formData
      ?.taxResidencyAdditionalInformation as TTaxResidencyAdditionalInformation
  )?.controllingPersons || [];

export const selectAccountHolderStatusSelectedOption = (state: RootState) =>
  (
    state.identification?.formData
      ?.taxResidencyAdditionalInformation as TTaxResidencyAdditionalInformation
  )?.accountHolderStatus?.selectedOption || null;

export const selectFatcaAdditionalInformation = (state: RootState) =>
  (state.identification.formData?.fatcaAdditionalInformation as TFatcaAdditionalInformation) ??
  EMPTY_FATCA_ADDITIONAL_INFORMATION;

export const selectGeneralInfoLegalResident = (state: RootState) =>
  state.identification.formData?.generalInfo as TGeneralInfoLegalResident;
